import React, { useState, useEffect } from 'react';
import axiosInstance from '../utils/axiosInstance';
import { X, Plus, Trash2, AlertCircle, Loader2 } from 'lucide-react';

const ResumeFiltersModal = ({ resumeId, resumeTitle, isOpen, onClose, onUpdate }) => {
  const [filters, setFilters] = useState([]);
  const [newFilter, setNewFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen && resumeId) {
      loadFilters();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, resumeId]);

  const loadFilters = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axiosInstance.get(`/resumes/${resumeId}/filters`);
      setFilters(response.data.filters || []);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load filters');
    } finally {
      setLoading(false);
    }
  };

  const handleAddFilter = async () => {
    if (!newFilter.trim()) return;
    if (filters.includes(newFilter.trim())) {
      setError('Filter already exists');
      return;
    }

    const updatedFilters = [...filters, newFilter.trim()];
    await saveFilters(updatedFilters);
    setNewFilter('');
  };

  const handleDeleteFilter = async (filterText) => {
    try {
      await axiosInstance.delete(`/resumes/${resumeId}/filters/${encodeURIComponent(filterText)}`);
      const updatedFilters = filters.filter(f => f !== filterText);
      setFilters(updatedFilters);
      if (onUpdate) onUpdate(updatedFilters);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to delete filter');
    }
  };

  const saveFilters = async (updatedFilters) => {
    setLoading(true);
    setError('');
    try {
      await axiosInstance.post(`/resumes/${resumeId}/filters`, { filters: updatedFilters });
      setFilters(updatedFilters);
      if (onUpdate) onUpdate(updatedFilters);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to save filters');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleAddFilter();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-200" onClick={onClose}>
      <div 
        className="w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200" 
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
           <div>
              <h3 className="text-lg font-bold text-slate-900">Job Title Filters</h3>
              <p className="text-xs text-slate-500 mt-0.5">Resume: {resumeTitle}</p>
           </div>
           <button 
              className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-full transition-all" 
              onClick={onClose}
           >
              <X size={20} />
           </button>
        </div>

        <div className="p-6">
           {error && (
             <div className="mb-4 p-3 bg-red-50 border border-red-100 rounded-xl flex items-start gap-2 text-sm text-red-700">
                <AlertCircle size={16} className="shrink-0 mt-0.5" />
                <span className="font-medium">{error}</span>
             </div>
           )}

           <div className="flex gap-2 mb-6">
              <div className="relative flex-1">
                 <input
                    type="text"
                    className="w-full pl-3 pr-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:bg-white focus:ring-2 focus:ring-blue-100 focus:border-blue-500 outline-none transition-all"
                    placeholder="e.g., Full Stack Developer"
                    value={newFilter}
                    onChange={(e) => setNewFilter(e.target.value)}
                    onKeyPress={handleKeyPress}
                    disabled={loading}
                 />
              </div>
              <button
                 className="px-4 py-2 bg-blue-600 text-white rounded-xl text-sm font-bold hover:bg-blue-700 disabled:opacity-50 transition-all flex items-center gap-2 shadow-md shadow-blue-100"
                 onClick={handleAddFilter}
                 disabled={loading || !newFilter.trim()}
              >
                 <Plus size={18} /> Add
              </button>
           </div>

           <div className="space-y-3 max-h-[300px] overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-slate-200">
              {loading && !filters.length ? (
                 <div className="flex flex-col items-center justify-center py-12 text-slate-400">
                    <Loader2 size={32} className="animate-spin mb-2" />
                    <span className="text-sm">Loading filters...</span>
                 </div>
              ) : filters.length === 0 ? (
                 <div className="text-center py-12 px-4 border-2 border-dashed border-slate-100 rounded-2xl bg-slate-50/50">
                    <p className="text-sm text-slate-500">No filters yet. Add job titles to automatically select this resume for matching jobs.</p>
                 </div>
              ) : (
                 filters.map((filter, index) => (
                    <div 
                      key={index} 
                      className="flex items-center justify-between p-3 bg-white border border-slate-200 rounded-xl group hover:border-blue-200 hover:shadow-sm transition-all animate-in slide-in-from-bottom-2 duration-200"
                    >
                       <span className="text-sm font-medium text-slate-700">{filter}</span>
                       <button
                          className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                          onClick={() => handleDeleteFilter(filter)}
                          disabled={loading}
                          aria-label={`Delete filter: ${filter}`}
                       >
                          <Trash2 size={16} />
                       </button>
                    </div>
                 ))
              )}
           </div>
        </div>

        <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex justify-end">
           <button 
              className="px-6 py-2 bg-white text-slate-700 font-bold border border-slate-300 rounded-xl text-sm hover:bg-slate-50 transition-all" 
              onClick={onClose}
           >
              Close
           </button>
        </div>
      </div>
    </div>
  );
};

export default ResumeFiltersModal;
